// iter149 — Slim top banner shown when navigator.onLine is false and when
// there are queued offline Daily Entry saves. Rendered mobile+desktop
// inside App.js, high enough on the stack that it sits above everything
// else (z-[80]). Pulls queued count from idb-keyval via listQueuedEntries.
import { useEffect, useState, useCallback } from "react";
import { WifiOff, CloudUpload, AlertTriangle } from "lucide-react";
import { listQueuedEntries } from "@/lib/dailyEntryQueue";

const EVENT_QUEUE_CHANGED = "mdm:offline-queue-changed";

export function bumpOfflineQueue() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(EVENT_QUEUE_CHANGED));
}

export default function OfflineBanner() {
  const [online, setOnline] = useState(() => (typeof navigator === "undefined" ? true : navigator.onLine));
  const [count, setCount] = useState(0);
  const [stuckCount, setStuckCount] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const items = await listQueuedEntries();
      setCount(items.length);
      setStuckCount(items.filter((i) => i.stuck).length);
    } catch { /* idb unavailable */ }
  }, []);

  useEffect(() => {
    refresh();
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    window.addEventListener(EVENT_QUEUE_CHANGED, refresh);
    const t = setInterval(refresh, 15000);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      window.removeEventListener(EVENT_QUEUE_CHANGED, refresh);
      clearInterval(t);
    };
  }, [refresh]);

  if (online && count === 0) return null;
  const stuck = stuckCount > 0;
  return (
    <div
      data-testid="offline-banner"
      className={`fixed inset-x-0 top-0 z-[80] flex items-center justify-center gap-2 border-b px-3 py-1.5 text-xs font-semibold ${
        !online
          ? "border-amber-200 bg-amber-100 text-amber-900"
          : stuck
            ? "border-rose-200 bg-rose-100 text-rose-900"
            : "border-sky-200 bg-sky-100 text-sky-900"
      }`}
      style={{ paddingTop: "calc(0.375rem + env(safe-area-inset-top, 0px))" }}
    >
      {!online ? (
        <><WifiOff className="h-3.5 w-3.5" /> You are offline — entries save on this phone and will sync when back online.</>
      ) : stuck ? (
        <><AlertTriangle className="h-3.5 w-3.5" /> {stuckCount} offline {stuckCount === 1 ? "entry" : "entries"} couldn't sync — open Daily Entry to retry.</>
      ) : (
        <><CloudUpload className="h-3.5 w-3.5" /> Syncing {count} offline {count === 1 ? "entry" : "entries"}…</>
      )}
    </div>
  );
}
